import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Section, Toggle } from '../settingsPrimitives';
import { usePreferencesStore } from '../../store/preferencesStore';
import { TYPE_CATEGORIES, normalizeDbTypeColors, typeColor } from './dbTypeColors';

/**
 * Settings → Extensions → DB Manager → "Value colours": the switch, a colour
 * per value type for each theme, and a live preview of both (owner,
 * 30.09.2026: "toggleable + configurable", HeidiSQL-style).
 */
export default function DbTypeColorSettings() {
  const { t } = useTranslation();
  const raw = usePreferencesStore((s) => s.dbTypeColors);
  const update = usePreferencesStore((s) => s.updatePreference);
  const pref = normalizeDbTypeColors(raw);
  const save = (next) => update('dbTypeColors', normalizeDbTypeColors(next));
  const setColor = (theme, id, hex) => save({ ...pref, [theme]: { ...pref[theme], [id]: hex } });
  const customised = Object.keys(pref.light).length + Object.keys(pref.dark).length > 0;
  const label = t('dbManagerExt.typeColors', 'Colour values by type');

  return (
    <Section title={t('dbManagerExt.valueColoursSection', 'Value colours')}>
      <Row
        label={label}
        desc={t(
          'dbManagerExt.typeColorsDesc',
          'Show numbers, text, dates, JSON, NULL… each in its own colour in the grid, like HeidiSQL.',
        )}
      >
        <Toggle value={pref.enabled} onChange={(on) => save({ ...pref, enabled: on })} />
      </Row>
      {pref.enabled && (
        <div className="dbm-typecolor-table" role="table" aria-label={t('dbManagerExt.typeColorsTable', 'Colours per value type')}>
          <div className="dbm-typecolor-row dbm-typecolor-head" role="row">
            <span role="columnheader">{t('dbManagerExt.valueType', 'Type')}</span>
            <span role="columnheader">{t('dbManagerExt.light', 'Light')}</span>
            <span role="columnheader">{t('dbManagerExt.dark', 'Dark')}</span>
          </div>
          {TYPE_CATEGORIES.map(({ id, label: typeLabel, sample }) => (
            <div className="dbm-typecolor-row" role="row" key={id}>
              <span role="cell" className="dbm-typecolor-name">{typeLabel}</span>
              {['light', 'dark'].map((theme) => (
                <span role="cell" key={theme} className={`dbm-typecolor-cell dbm-typecolor-cell--${theme}`}>
                  <input
                    type="color"
                    aria-label={`${typeLabel} — ${theme}`}
                    value={typeColor(pref, theme, id)}
                    onChange={(e) => setColor(theme, id, e.target.value)}
                  />
                  <code style={{ color: typeColor(pref, theme, id) }}>{sample}</code>
                </span>
              ))}
            </div>
          ))}
          <div className="dbm-typecolor-actions">
            <button
              type="button"
              className="dbm-pager-btn"
              disabled={!customised}
              onClick={() => save({ enabled: pref.enabled, light: {}, dark: {} })}
            >
              {t('dbManagerExt.resetColours', 'Reset to defaults')}
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}
